import { useEffect, useRef } from 'react'

import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'

import { useWaitingRoomJoined } from './useWaitingRoomJoined'

/**
 * Detects newly-arrived patients in a waiting room and invokes `onNewPatients(count)` once per
 * arrival, where `count` is how many patients appeared since the previous observation.
 *
 * Shared by the sound and browser-notification hooks so both react to the SAME signal, and the
 * "is the doctor a member of this room" gating + baseline bookkeeping live in a single place.
 *
 * Baseline rules:
 *  - The first time the room is observed, the existing queue is baselined SILENTLY — a backlog that
 *    was already waiting when the page opened must not trigger an alert.
 *  - While the doctor is not a member of the room, the baseline is FROZEN (neither moved nor reset):
 *    patients who arrive during a brief presence flap / socket reconnect are still announced once
 *    membership is restored, instead of being silently swallowed. (The previous implementation reset
 *    the baseline to the current count on every not-joined render, which lost those arrivals.)
 */
export function useNewPatients(waitingRoomId: string, onNewPatients: (newPatientsCount: number) => void) {
  const { data: waitingRoom } = useGetWaitingRoomQuery(waitingRoomId, { refetchOnMountOrArgChange: true })
  const waitingRoomJoined = useWaitingRoomJoined(waitingRoom)
  const consultationsCount = waitingRoom?.consultations.length ?? 0

  const baselineRef = useRef<number | null>(null)
  // Keep the latest callback in a ref so the effect can stay keyed on the patient count only.
  const onNewPatientsRef = useRef(onNewPatients)
  onNewPatientsRef.current = onNewPatients

  useEffect(() => {
    if (!waitingRoom) return

    // Silent baseline on the first observation of the room (do not announce the existing backlog).
    if (baselineRef.current === null) {
      baselineRef.current = consultationsCount
      return
    }

    // Freeze the baseline while not a member: do not announce, do not move it.
    if (!waitingRoomJoined) return

    if (consultationsCount > baselineRef.current) {
      onNewPatientsRef.current(consultationsCount - baselineRef.current)
    }
    baselineRef.current = consultationsCount
  }, [consultationsCount, waitingRoomJoined, waitingRoom])
}
